/**
 * Knopf für die technische Beschreibung (Mathias, 11/2026).
 *
 * Die PDF-Bibliothek wird erst beim Drücken geladen — sie ist größer als der
 * Rest der Ausgabe, und gebraucht wird sie selten. Die Datenblätter brauchen
 * Verbindung; ohne sie entsteht die Beschreibung trotzdem, und welche Blätter
 * fehlen, steht im Dokument und hier.
 *
 * Heruntergeladen wird erst auf einen zweiten Tipp: Safari lässt einen
 * Download, der nach einem längeren Abruf von selbst startet, gern liegen.
 * Am iPad gibt es zusätzlich „Teilen“ — in Dateien sichern oder gleich an den
 * Kunden mailen.
 */

import { useEffect, useState } from 'react';

import { rufeServer } from '../daten/abgleich.ts';
import { datenblattname, useDatenblaetter } from '../daten/datenblaetter.ts';
import type { Beschreibungseingabe } from '../export/beschreibung.ts';
import type { Beilage } from '../export/pdf.ts';

interface Ergebnis {
  url: string;
  datei: File;
  seiten: number;
  beigelegt: number;
  fehlend: Beilage[];
}

export function Beschreibungsexport({ eingabe }: { eingabe: () => Beschreibungseingabe }): React.JSX.Element {
  const datenblaetter = useDatenblaetter();
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Ergebnis | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => () => {
    if (ergebnis) URL.revokeObjectURL(ergebnis.url);
  }, [ergebnis]);

  async function erstelle(): Promise<void> {
    setFehler(null);
    setErgebnis(null);
    try {
      setLaeuft('Bereite vor …');
      const [{ inhalt, datenblaetterFuer, dateinameFuer }, { erstellePdf }] = await Promise.all([
        import('../export/beschreibung.ts'),
        import('../export/pdf.ts'),
      ]);
      const e = eingabe();
      const wuensche = datenblaetterFuer(e);
      const beilagen: Beilage[] = [];
      for (const [i, w] of wuensche.entries()) {
        const blatt = datenblaetter.find((d) => d.id === w.datenblattId);
        const titel = blatt ? datenblattname(blatt) : 'Datenblatt';
        setLaeuft(`Datenblatt ${i + 1} von ${wuensche.length} …`);
        // Auch ohne Empfang versuchen: Ein Datenblatt, das schon einmal
        // geöffnet wurde, liegt im Zwischenspeicher des Browsers (der Server
        // gibt es sieben Tage frei — jedes Hochladen bekommt eine neue Kennung).
        try {
          const antwort = await rufeServer(`/api/datenblaetter/${w.datenblattId}/datei`);
          if (!antwort.ok) throw new Error(`nicht geladen (${antwort.status})`);
          const mime = blatt?.mime ?? antwort.headers.get('Content-Type') ?? 'application/pdf';
          beilagen.push({ titel, wofuer: w.wofuer, daten: { bytes: new Uint8Array(await antwort.arrayBuffer()), mime } });
        } catch (f) {
          beilagen.push({
            titel,
            wofuer: w.wofuer,
            fehler: navigator.onLine ? (f instanceof Error ? f.message : String(f)) : 'keine Verbindung',
          });
        }
      }
      setLaeuft('Setze das Dokument …');
      const fertig = await erstellePdf(inhalt(e), beilagen, { autor: e.errichter.firma });
      const datei = new File([fertig.bytes as Uint8Array<ArrayBuffer>], dateinameFuer(e.projekt, e.stand), { type: 'application/pdf' });
      setErgebnis({
        url: URL.createObjectURL(datei),
        datei,
        seiten: fertig.seiten,
        beigelegt: fertig.beigelegt,
        fehlend: fertig.fehlend,
      });
    } catch (f) {
      setFehler(f instanceof Error ? f.message : String(f));
    } finally {
      setLaeuft(null);
    }
  }

  const teilbar =
    !!ergebnis && typeof navigator.canShare === 'function' && navigator.canShare({ files: [ergebnis.datei] });

  return (
    <section>
      <h2 style={{ marginTop: '1.5rem' }}>Technische Beschreibung</h2>
      <p className="klein gedaempft" style={{ margin: '0 0 0.6rem' }}>
        Ein PDF mit Anlage, Belegung und Stringplan, Komponenten, Leitungswegen und Prüfbefund — dahinter
        die Datenblätter der verbauten Module und Geräte. Für die Datenblätter braucht es Verbindung.
      </p>
      <div className="knopfreihe">
        <button className="wichtig" disabled={!!laeuft} onClick={() => void erstelle()}>
          {laeuft ?? (ergebnis ? 'Neu erstellen' : 'PDF erstellen')}
        </button>
      </div>
      {fehler && <div className="hinweis hinweis--fehler">Nicht erstellt: {fehler}</div>}
      {ergebnis && (
        <div className="hinweis hinweis--gut">
          Fertig: {ergebnis.seiten} Seiten, {ergebnis.beigelegt} Datenblatt/-blätter beigelegt.
          {ergebnis.fehlend.length > 0 && (
            <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.1rem' }}>
              {ergebnis.fehlend.map((f) => (
                <li key={f.titel}>
                  Nicht beigelegt: {f.titel} — {f.fehler ?? 'nicht geladen'}
                </li>
              ))}
            </ul>
          )}
          <div className="knopfreihe" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
            <a className="knopf" href={ergebnis.url} download={ergebnis.datei.name}>
              Herunterladen
            </a>
            <a className="knopf" href={ergebnis.url} target="_blank" rel="noreferrer">
              Öffnen
            </a>
            {teilbar && (
              <button
                onClick={() =>
                  void navigator.share({ files: [ergebnis.datei], title: ergebnis.datei.name }).catch(() => undefined)
                }
              >
                Teilen
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
